import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  useTheme,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../utils/api';
import logger from '../utils/logger';

const JsonField = ({ name, value }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  if (typeof value !== 'object' || value === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Typography sx={{ color: theme.palette.text.secondary, minWidth: 150 }}>
          {name}:
        </Typography>
        <Typography sx={{ ml: 2, wordBreak: 'break-all' }}>
          {String(value)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <IconButton size="small">
          {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
        </IconButton>
        <Typography sx={{ color: theme.palette.text.secondary }}>
          {name}
        </Typography>
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 4, pt: 1 }}>
          {Object.entries(value).map(([key, val]) => (
            <JsonField key={key} name={key} value={val} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const JsonDisplay = ({ data, title, loading = false, error = null }) => {
  const theme = useTheme();
  
  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary 
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          }
        }}
      >
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Paper 
          sx={{ 
            p: 3, 
            backgroundColor: theme.palette.grey[100],
            position: 'relative'
          }}
          elevation={0}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            Object.entries(data).map(([key, value]) => (
              <JsonField key={key} name={key} value={value} />
            ))
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

const AuthDebug = () => {
  const [open, setOpen] = useState(false);
  const [serverResponse, setServerResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser, session } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    const fetchAuthDebug = async () => {
      if (!open) return;
      
      setLoading(true);
      setError(null);
      try {
        const response = await apiGet('/authdebug');
        setServerResponse(response);
      } catch (err) {
        logger.error('Error fetching auth debug info:', err);
        // Check if it's a permission error (403)
        if (err.status === 403) {
          setError('Insufficient permissions - Only admin users can view debug information');
        } else {
          const errorData = await err.response?.json().catch(() => ({}));
          setError(errorData?.error || 'Failed to fetch server debug info');
        }
        setServerResponse(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthDebug();
  }, [open]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <>
      <span 
        onClick={toggleDrawer}
        className="nav-text"
      >
        Auth Debug
      </span>
      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            width: '65%',
            height: '100vh',
            overflowY: 'auto'
          }
        }}
      >
        <Box sx={{ 
          p: 4,
          height: '100%'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}>
            <Typography variant="h4" gutterBottom>
              Auth Debug Info
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'pointer',
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main
                }
              }}
              onClick={toggleDrawer}
            >
              Close 
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <JsonDisplay 
              title="User Info" 
              data={currentUser || {}} 
            />

            <JsonDisplay 
              title="Session Info" 
              data={session || {}} 
            />

            <JsonDisplay 
              title="Server Response" 
              data={serverResponse || {}}
              loading={loading}
              error={error}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AuthDebug;
